// @ts-check
import './svg-map.css';
import { h } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

import Shape from './components/Shape';
import PlotLabel from './components/PlotLabel';
import Tooltip from './components/Tooltip';
import PlotNumberTooltip from './components/PlotNumberTooltip';
import CustomShapeContent from './components/CustomShapeContent';

import { getGroupById } from '../../utils/groupHelpers';
import { getPlotNumbersByGroup } from '../../utils/plotNumberHelpers';
import { getPlotNumberIdFromShapeLink, getGroupIdFromShapeLink } from '../../utils/svgMapHelpers';

/**
 * @param {string} link
 * @param {import('../../types').PlotNumber[]} plotNumbers
 * @returns {import('../../types').PlotNumber=}
 */
const getPlotNumberById = (link, plotNumbers) => plotNumbers.find(({ id: plotId }) => getPlotNumberIdFromShapeLink(link) === plotId);

/**
 * @param {string} id
 * @param {{id: string, coordinates: SVGRect}[]} shapeBBoxes
 * @returns {SVGRect=}
 */
const getBBox = (id, shapeBBoxes) => shapeBBoxes.find(shapeBBox => shapeBBox.id === id)?.coordinates;

/**
 * @param {object} props
 * @param {import('../../types').Shape[]} props.shapes
 * @param {import('../../types').PlotNumber[]} props.plotNumbers
 * @param {import('../../types').Group[]} props.groups
 * @param {import('../../types').Status[]} props.statuses
 * @param {{ width: number, height: number }} props.mapSize
 * @param {{ width: number, height: number }} props.svgSize
 * @param {string} props.backgroundImageSrc
 * @param {(shapeId: string, bBox: SVGRect) => void=} props.handleShapeClick
 * @param {(shapeId: string, bBox: SVGRect) => void=} props.handleShapeMouseEnter
 * @param {(shapeId: string, bBox: SVGRect) => void=} props.handleShapeMouseLeave
 * @param {(e: Event) => void=} props.handleOutsidePlotClick
 * @param {(svgRect: SVGRect) => void=} props.onSvgBoundingBoxChange
 * @param {boolean=} props.displayLotNumbers
 * @returns
 */
export default function SvgMap({
	shapes,
	plotNumbers,
	groups,
	statuses,
	mapSize,
	svgSize,
	backgroundImageSrc,
	handleShapeClick,
	handleShapeMouseEnter,
	handleShapeMouseLeave,
	handleOutsidePlotClick,
	onSvgBoundingBoxChange,
	displayLotNumbers = false
}) {
	const [plotSvgLabels, setPlotSvgLabels] = useState([]);
	const [bBoxes, setBBoxes] = useState([]);
	const refContainer = useRef(null);
	const [shapesAndPlotNumbers, setShapesAndPlotNumbers] = useState([]);

	const setRefSvg = useCallback((svgNode) => {
		if (!svgNode) {
			return;
		}

		if (typeof onSvgBoundingBoxChange === 'function') {
			onSvgBoundingBoxChange(svgNode.getBBox());
		}

		setBBoxes(Object.values(svgNode.children).map(child => ({
			id: child.getAttribute('id'),
			coordinates: child.getBBox()
		})).filter(child => child.id));
	}, [onSvgBoundingBoxChange]);

	useEffect(() => {
		if (plotNumbers.length <= 0 || bBoxes.length <= 0) {
			return;
		}

		setPlotSvgLabels(shapes.map(shape => {
			let coordinates = null;

			bBoxes.forEach(svgElement => {
				if (svgElement.id === shape.id) {
					coordinates = svgElement.coordinates;
				}
			});

			const plotNumber = getPlotNumberById(shape.link, plotNumbers);
			return {
				id: shape.id,
				label: plotNumber ? plotNumber.number : null,
				isHighlighted: shape.isHighlighted,
				coordinates
			};
		}));
	}, [bBoxes, shapes, plotNumbers]);

	useEffect(() => {
		setShapesAndPlotNumbers(shapes.map(shape => ({
			shape,
			plotNumber: getPlotNumberById(shape.link, plotNumbers),
			group: getGroupIdFromShapeLink(shape.link) ? {
				name: getGroupById(getGroupIdFromShapeLink(shape.link), groups)?.label || '',
				plotNumbers: getPlotNumbersByGroup(
					getGroupById(
						getGroupIdFromShapeLink(shape?.link), groups
					),
					plotNumbers
				),
			} : undefined
		})).filter(({ plotNumber, group }) => plotNumber || group));
	}, [shapes, plotNumbers, groups]);

	return (
		<div ref={refContainer} class="svg-map-container" style={`background-image: url('${backgroundImageSrc}'); width: ${mapSize.width}px; height: ${mapSize.height}px`} onClick={handleOutsidePlotClick}>
			{Array.isArray(shapesAndPlotNumbers) && shapesAndPlotNumbers.length > 0 &&
				<svg
					ref={setRefSvg}
					className="svg-map-shapes-container"
					version="1.1"
					xmlns="http://www.w3.org/2000/svg"
					xmlnsXlink="http://www.w3.org/1999/xlink"
					x="0px"
					y="0px"
					xmlSpace="preserve"
					viewBox={`0 0 ${svgSize.width} ${svgSize.height}`}
					width={svgSize.width}
					height={svgSize.height}
				>
					{
						shapesAndPlotNumbers.map(({ shape, plotNumber, group }) => (
							refContainer.current && <Tooltip
								content={plotNumber ?
									<PlotNumberTooltip {...plotNumber} /> :
									<CustomShapeContent name={group.name} plotNumbers={group.plotNumbers} statuses={statuses} />}
								key={shape.id}
								rootNode={refContainer.current}
								svgSize={svgSize}
							>
								<Shape
									{...shape}
									key={shape.id}
									bBox={getBBox(shape.id, bBoxes)}
									handleClick={handleShapeClick}
									handleMouseEnter={handleShapeMouseEnter}
									handleMouseLeave={handleShapeMouseLeave}
								/>
							</Tooltip>
						))
					}
					{
						displayLotNumbers && plotSvgLabels.length > 0 &&
						<g>
							{plotSvgLabels.map(plotLabel => (
								<PlotLabel
									{...plotLabel}
								/>
							))}
						</g>
					}
				</svg>}
		</div>
	);
}
